// src/services/staff-export.service.ts

import type { StaffMember, StaffPermissions } from '../types/api.types';

/**
 * Service d'export des données du personnel
 */
export class StaffExportService {
  /**
   * Exporter les données du personnel en CSV
   */
  exportToCSV(staff: StaffMember[], filename: string = 'personnel_avec.csv'): void {
    try {
      const headers = [
        'Numéro Personnel',
        'Prénom',
        'Nom',
        'Email',
        'Téléphone',
        'Rôle',
        'Département',
        'Poste',
        'Date Embauche',
        'Salaire',
        'Statut',
        'Email Vérifié',
        'Dernière Connexion',
        'Date Création',
        'Notes'
      ];

      const csvContent = [
        headers.join(','),
        ...staff.map(member => [
          `"${member.staffNumber}"`,
          `"${member.User.firstName}"`,
          `"${member.User.lastName}"`,
          `"${member.User.email}"`,
          `"${member.User.phone || ''}"`,
          `"${this.getRoleLabel(member.role)}"`,
          `"${member.department || ''}"`,
          `"${member.position || ''}"`,
          `"${new Date(member.hireDate).toLocaleDateString('fr-FR')}"`,
          `"${member.salary ? member.salary.toLocaleString() + ' CFA' : ''}"`,
          `"${member.isActive ? 'Actif' : 'Inactif'}"`,
          `"${member.User.emailVerified ? 'Oui' : 'Non'}"`,
          `"${member.User.lastLoginAt ? new Date(member.User.lastLoginAt).toLocaleString('fr-FR') : 'Jamais'}"`,
          `"${new Date(member.createdAt).toLocaleDateString('fr-FR')}"`,
          `"${member.notes || ''}"`
        ].join(','))
      ].join('\n');

      this.downloadFile(csvContent, filename, 'text/csv');
    } catch (error) {
      console.error('Erreur lors de l\'export CSV:', error);
      throw new Error('Échec de l\'export CSV');
    }
  }

  /**
   * Exporter les données du personnel en JSON
   */
  exportToJSON(staff: StaffMember[], filename: string = 'personnel_avec.json'): void {
    try {
      const exportData = {
        exportDate: new Date().toISOString(),
        totalRecords: staff.length,
        staff: staff.map(member => ({
          staffNumber: member.staffNumber,
          personalInfo: {
            firstName: member.User.firstName,
            lastName: member.User.lastName,
            email: member.User.email,
            phone: member.User.phone,
            emailVerified: member.User.emailVerified,
          },
          professionalInfo: {
            role: member.role,
            department: member.department,
            position: member.position,
            hireDate: member.hireDate,
            salary: member.salary,
            isActive: member.isActive,
          },
          systemInfo: {
            lastLoginAt: member.User.lastLoginAt,
            createdAt: member.createdAt,
            updatedAt: member.updatedAt,
          },
          permissions: member.permissions,
          notes: member.notes,
        }))
      };

      const jsonContent = JSON.stringify(exportData, null, 2);
      this.downloadFile(jsonContent, filename, 'application/json');
    } catch (error) {
      console.error('Erreur lors de l\'export JSON:', error);
      throw new Error('Échec de l\'export JSON');
    }
  }

  /**
   * Exporter un rapport détaillé des permissions
   */
  exportPermissionsReport(staff: StaffMember[], filename: string = 'permissions_personnel.csv'): void {
    try {
      const allPermissions = this.getAllUniquePermissions(staff);
      
      const headers = [
        'Numéro Personnel',
        'Nom Complet',
        'Rôle',
        'Département',
        ...allPermissions,
        'Limites Crédit',
        'Limites Épargne',
        'Limites Solidarité'
      ];

      const csvContent = [
        headers.join(','),
        ...staff.map(member => {
          const permissions = member.permissions;
          const permissionValues = allPermissions.map(perm => 
            permissions[perm as keyof StaffPermissions] ? 'Oui' : 'Non'
          );

          return [
            `"${member.staffNumber}"`,
            `"${member.User.firstName} ${member.User.lastName}"`,
            `"${this.getRoleLabel(member.role)}"`,
            `"${member.department || ''}"`,
            ...permissionValues.map(val => `"${val}"`),
            `"${this.formatApprovalLimit(permissions.approvalLimits?.credit)}"`,
            `"${this.formatApprovalLimit(permissions.approvalLimits?.savings)}"`,
            `"${this.formatApprovalLimit(permissions.approvalLimits?.solidarity)}"`
          ].join(',');
        })
      ].join('\n');

      this.downloadFile(csvContent, filename, 'text/csv');
    } catch (error) {
      console.error('Erreur lors de l\'export des permissions:', error);
      throw new Error('Échec de l\'export des permissions');
    }
  }

  /**
   * Exporter les statistiques du personnel
   */
  exportStatsReport(staff: StaffMember[], filename: string = 'statistiques_personnel.json'): void {
    try {
      const stats = this.calculateStaffStats(staff);
      const jsonContent = JSON.stringify(stats, null, 2);
      this.downloadFile(jsonContent, filename, 'application/json');
    } catch (error) {
      console.error('Erreur lors de l\'export des statistiques:', error);
      throw new Error('Échec de l\'export des statistiques');
    }
  }

  /**
   * Calculer les statistiques du personnel
   */
  private calculateStaffStats(staff: StaffMember[]) {
    const total = staff.length;
    const active = staff.filter(s => s.isActive).length;
    const roles = staff.reduce((acc, s) => {
      acc[s.role] = (acc[s.role] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const departments = staff.reduce((acc, s) => {
      const dept = s.department || 'Non assigné';
      acc[dept] = (acc[dept] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const withSalary = staff.filter(s => s.salary).length;
    const averageSalary = staff
      .filter(s => s.salary)
      .reduce((sum, s) => sum + (s.salary || 0), 0) / withSalary || 0;

    const emailVerified = staff.filter(s => s.User.emailVerified).length;
    const withPhone = staff.filter(s => s.User.phone).length;
    const everLoggedIn = staff.filter(s => s.User.lastLoginAt).length;

    return {
      exportDate: new Date().toISOString(),
      summary: {
        totalStaff: total,
        activeStaff: active,
        inactiveStaff: total - active,
        activePercentage: total > 0 ? ((active / total) * 100).toFixed(1) : '0',
      },
      byRole: roles,
      byDepartment: departments,
      salaryInfo: {
        staffWithSalary: withSalary,
        averageSalary: Math.round(averageSalary),
        totalSalaryBudget: staff.reduce((sum, s) => sum + (s.salary || 0), 0),
      },
      systemUsage: {
        emailVerified,
        emailVerificationRate: total > 0 ? ((emailVerified / total) * 100).toFixed(1) : '0',
        withPhone,
        phoneRate: total > 0 ? ((withPhone / total) * 100).toFixed(1) : '0',
        everLoggedIn,
        loginRate: total > 0 ? ((everLoggedIn / total) * 100).toFixed(1) : '0',
      },
    };
  }

  /**
   * Obtenir toutes les permissions uniques du personnel
   */
  private getAllUniquePermissions(staff: StaffMember[]): string[] {
    const allPermissions = new Set<string>();
    
    staff.forEach(member => {
      // Type assertion to handle string index access
      const permissions = member.permissions as { [key: string]: any };
      Object.keys(permissions).forEach(permission => {
        if (permission !== 'approvalLimits' && typeof permissions[permission] === 'boolean') {
          allPermissions.add(permission);
        }
      });
    });

    return Array.from(allPermissions).sort();
  }

  /**
   * Formater les limites d'approbation
   */
  private formatApprovalLimit(limit: any): string {
    if (!limit) return 'Non défini';
    if (limit === 'unlimited') return 'Illimité';
    if (typeof limit === 'number') return `${limit.toLocaleString()} CFA`;
    return limit.toString();
  }

  /**
   * Obtenir le libellé du rôle
   */
  private getRoleLabel(role: string): string {
    const roleLabels: Record<string, string> = {
      administrator: 'Administrateur',
      accountant: 'Comptable',
      credit_agent: 'Agent de crédit'
    };
    return roleLabels[role] || role;
  }

  /**
   * Télécharger un fichier
   */
  private downloadFile(content: string, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Nettoyer l'URL de l'objet
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }

  /**
   * Valider les données avant export
   */
  validateExportData(staff: StaffMember[]): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!staff || staff.length === 0) {
      errors.push('Aucune donnée de personnel à exporter');
    }

    // Vérifier l'intégrité des données essentielles
    staff.forEach((member, index) => {
      if (!member.User?.firstName || !member.User?.lastName) {
        errors.push(`Ligne ${index + 1}: Nom ou prénom manquant`);
      }
      if (!member.User?.email) {
        errors.push(`Ligne ${index + 1}: Email manquant`);
      }
      if (!member.staffNumber) {
        errors.push(`Ligne ${index + 1}: Numéro de personnel manquant`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Export singleton instance
const staffExportService = new StaffExportService();
export default staffExportService;